import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext";
import { getData, postData, serverURL } from "../../../../services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// ICONS (optimized sizes)
// ─────────────────────────────────────────────────────────────────────────────
const EditIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const TrashIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);
const SearchIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);
const ChevronLeftIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>);
const ChevronRightIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>);
const PlusIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const ViewMoreIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>);
const CheckCircleIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>);
const XCircleIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>);
const PowerIcon = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></svg>);
const BackIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>);
const OnlineIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/><path d="M4 4l2 2"/><path d="M20 4l-2 2"/></svg>);

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSION CHECK HELPER
// ─────────────────────────────────────────────────────────────────────────────
const checkPermission = (permission) => {
  try {
    const permissionsStr = localStorage.getItem('userPermission') || localStorage.getItem('userPermissions');
    if (!permissionsStr) return false;
    
    let permissions = [];
    try {
      permissions = JSON.parse(permissionsStr);
    } catch (e) {
      permissions = permissionsStr.split(',').map(p => p.trim());
    }
    
    return permissions.includes(permission);
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE COMPONENT (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────
const DriverImage = ({ picName, name, theme }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const getImageUrl = () => {
    if (!picName || picName.trim() === "" || picName === "NULL" || picName === "null") {
      return null;
    }
    let filename = picName;
    if (filename.includes('/')) {
      filename = filename.split('/').pop();
    }
    if (filename.includes('\\')) {
      filename = filename.split('\\').pop();
    }
    return `${serverURL}/public/uploadnew/upload/${filename}`;
  };
  
  const imageUrl = getImageUrl();
  
  if (!imageUrl || imageError) {
    const initial = name ? name.charAt(0).toUpperCase() : "D";
    return (
      <div style={{ 
        width: "32px", 
        height: "32px", 
        borderRadius: "50%", 
        background: theme.primaryGradient || "linear-gradient(135deg, #3b82f6, #8b5cf6)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        fontSize: "13px", 
        fontWeight: 600, 
        color: "#fff" 
      }}>
        {initial}
      </div>
    );
  }
  
  return (
    <img 
      src={imageUrl}
      alt={name}
      onError={() => { setImageError(true); setIsLoading(false); }}
      onLoad={() => setIsLoading(false)}
      style={{
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        objectFit: "cover",
        border: `1px solid ${theme.border}`
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (Theme-Aware - Same as Restaurant)
// ─────────────────────────────────────────────────────────────────────────────

const StatusBadge = ({ loginBlocked, theme }) => {
  const config = {
    Allowed: { bg: `${theme.success}1a`, color: theme.success, dot: theme.success },
    Blocked: { bg: `${theme.error}1a`, color: theme.error, dot: theme.error },
  };
  const status = String(loginBlocked) === "1" ? "Blocked" : "Allowed";
  const c = config[status];
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px 2px 6px", borderRadius: "16px", background: c.bg, border: `1px solid ${c.bg}` }}>
      <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: c.dot }} />
      <span style={{ fontSize: "10px", fontWeight: 600, color: c.color }}>{status}</span>
    </div>
  );
};

const ReadyBadge = ({ is_ready, theme }) => {
  const ready = is_ready === "1" ? "Yes" : "No";
  const config = {
    Yes: { bg: `${theme.success}1a`, color: theme.success },
    No: { bg: `${theme.warning}1a`, color: theme.warning },
  };
  const c = config[ready] || config.No;
  return <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 600, background: c.bg, color: c.color }}>{ready}</span>;
};

const OnOffBadge = ({ open_status, theme }) => {
  const value = open_status === "1" ? "ON" : "OFF";
  const config = {
    ON: { bg: `${theme.success}1a`, color: theme.success },
    OFF: { bg: `${theme.textMuted}1a`, color: theme.textMuted },
  };
  const c = config[value] || config.OFF;
  return <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 700, background: c.bg, color: c.color }}>{value}</span>;
};

const VerificationBadge = ({ driver, theme }) => {
  const isVerified = driver.is_verify_personalinfo === "1" && driver.is_verify_docinfo === "1" && driver.is_verify_vehicle === "1" && driver.is_verify_bankinfo === "1" && driver.is_verify_emergency === "1";
  const verifiedColor = theme.success;
  const pendingColor = theme.warning;
  return (
    <span style={{ 
      padding: "2px 8px", 
      borderRadius: "10px", 
      fontSize: "10px", 
      fontWeight: 600, 
      background: isVerified ? `${verifiedColor}1a` : `${pendingColor}1a`, 
      color: isVerified ? verifiedColor : pendingColor 
    }}>
      {isVerified ? "Verified" : "Pending"}
    </span>
  );
};

const Checkbox = ({ checked, onChange, theme }) => (
  <div onClick={onChange} style={{
    width: "14px", height: "14px", borderRadius: "3px",
    border: checked ? `2px solid ${theme.primary}` : `2px solid ${theme.border}`,
    background: checked ? theme.primary : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", transition: "all 0.15s ease",
  }}>
    {checked && <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
  </div>
);

const Toast = ({ message, type, onClose, theme }) => {
  const bg = type === "error" ? `${theme.error}1a` : `${theme.success}1a`;
  const border = type === "error" ? `${theme.error}40` : `${theme.success}40`;
  const textColor = type === "error" ? theme.error : theme.success;
  const dotColor = type === "error" ? theme.error : theme.success;
  return (
    <div style={{ 
      position: "fixed", 
      bottom: "24px", 
      right: "24px", 
      zIndex: 999, 
      background: theme.surface, 
      border: `1px solid ${border}`, 
      borderRadius: "10px", 
      padding: "10px 16px", 
      display: "flex", 
      alignItems: "center", 
      gap: "8px", 
      boxShadow: `0 8px 32px ${theme.primaryLight}`, 
      animation: "slideUp 0.25s ease" 
    }}>
      <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: dotColor }} />
      <span style={{ fontSize: "12px", color: textColor, flex: 1 }}>{message}</span>
      <span onClick={onClose} style={{ cursor: "pointer", color: theme.textMuted, fontSize: "14px" }}>×</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT - DriverTable
// ─────────────────────────────────────────────────────────────────────────────
const DriverTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useContext(ThemeContext); // ← Restaurant-style theme object
  
  // Check permissions
  const hasOverviewPermission = checkPermission('drivers.overview');
  const hasCreatePermission = checkPermission('drivers.create');
  const hasDeletePermission = checkPermission('drivers.delete');
  const hasEditPermission = checkPermission('drivers.edit');
  
  // State
  const [drivers, setDrivers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [readyFilter, setReadyFilter] = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Pre-filtered data state
  const [isPreFiltered, setIsPreFiltered] = useState(false);
  const [filterTitle, setFilterTitle] = useState("");
  const [filterType, setFilterType] = useState("");

  const showToast = (message, type = "success") => { 
    setToast({ message, type }); 
    setTimeout(() => setToast(null), 3500); 
  };

  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleBack = () => {
    navigate("/Dashboard");
  };

  useEffect(() => { 
    const t = setTimeout(() => { 
      setDebouncedSearch(search); 
      setPage(1); 
    }, 500); 
    return () => clearTimeout(t); 
  }, [search]);

  const fetchDrivers = useCallback(async () => {
    if (!hasOverviewPermission) {
      setLoading(false);
      showToast("You don't have permission to view drivers", "error");
      return;
    }

    setLoading(true);
    try {
      const state = location.state;
      console.log("📍 DriverTable - Location state:", state);
      
      if (state && state.data && state.data.length > 0) {
        console.log("✅ Received pre-filtered driver data from Dashboard:", state);
        console.log("✅ Number of drivers:", state.data.length);
        
        const sortedData = [...state.data].sort((a, b) => {
          if (a.created_at && b.created_at) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return parseInt(b.id) - parseInt(a.id);
        });
        
        setDrivers(sortedData);
        setTotal(sortedData.length);
        setIsPreFiltered(true);
        setFilterTitle(state.title || "Online Riders");
        setFilterType(state.type || "online");
        setLoading(false);
        return;
      }
      
      const res = await getData("drivers/Riders");
      console.log("API Response:", res);
      
      let driversData = [];
      if (res && res.status === true) {
        driversData = res.data || [];
      } else if (Array.isArray(res)) {
        driversData = res;
      } else if (res && res.drivers && Array.isArray(res.drivers)) {
        driversData = res.drivers;
      } else if (res && res.data && Array.isArray(res.data)) {
        driversData = res.data;
      } else {
        driversData = [];
      }
      
      driversData = driversData.sort((a, b) => {
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return parseInt(b.id) - parseInt(a.id);
      });
      
      setDrivers(driversData);
      setTotal(driversData.length);
      setIsPreFiltered(false);
      setFilterTitle("");
      setFilterType("");
    } catch (error) {
      console.error("Fetch drivers error:", error);
      showToast(`Failed to load drivers: ${error.message}`, "error");
      setDrivers([]);
      setTotal(0);
    } finally { 
      setLoading(false); 
    }
  }, [location.state, hasOverviewPermission]);

  useEffect(() => { 
    fetchDrivers(); 
  }, [fetchDrivers]);

  const applySearch = (driver, searchTerm) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase().trim();
    
    const searchableFields = [
      driver.id?.toString() || "",
      driver.name || "",
      driver.first_name || "",
      driver.last_name || "",
      driver.displayName || "",
      driver.contact || "",
      driver.primary_number || "",
      driver.whatsapp_number || "",
      driver.email || "",
      driver.address || "",
      driver.city || "",
      driver.state || "",
      driver.postal || "",
      driver.bank_name || "",
      driver.account_number || "",
      driver.ifsc || "",
      driver.wallet || "",
      driver.is_ready === "1" ? "ready" : "",
      driver.is_ready === "0" ? "not ready" : "",
      String(driver.login_blocked) === "1" ? "login blocked logged out" : "login allowed",
      driver.gender || "",
      driver.blood_group || "",
    ];
    
    return searchableFields.some(field => 
      field && field.toString().toLowerCase().includes(term)
    );
  };

  let filteredDrivers = [...drivers];
  
  if (statusFilter !== "All") {
    filteredDrivers = filteredDrivers.filter(d => 
      statusFilter === "Allowed" ? String(d.login_blocked) !== "1" : String(d.login_blocked) === "1"
    );
  }
  
  if (readyFilter !== "All") {
    filteredDrivers = filteredDrivers.filter(d => 
      readyFilter === "Yes" ? d.is_ready === "1" : d.is_ready !== "1"
    );
  }
  
  if (debouncedSearch) {
    filteredDrivers = filteredDrivers.filter(driver => applySearch(driver, debouncedSearch));
  }
  
  const startIndex = (page - 1) * perPage;
  const paginatedDrivers = filteredDrivers.slice(startIndex, startIndex + perPage);
  const totalFiltered = filteredDrivers.length;
  const totalPages = Math.ceil(totalFiltered / perPage);

  const mappedDrivers = paginatedDrivers.map((d, idx) => ({ 
    ...d, 
    sn: totalFiltered - startIndex - idx,
    displayName: d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || `Driver ${d.id}`,
    displayWallet: `₹${parseFloat(d.wallet || 0).toLocaleString()}`,
    displayDate: d.created_at ? new Date(d.created_at).toLocaleDateString('en-IN') : d.rd || 'N/A',
    login_blocked: d.login_blocked || "0",
    is_ready: d.is_ready || "0",
    open_status: d.open_status || "0",
    contact: d.contact || d.primary_number || d.whatsapp_number || 'N/A',
  }));

  const pageIds = mappedDrivers.map(d => d.id?.toString());
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  
  const toggleAll = () => { 
    setSelected(prev => { 
      const next = new Set(prev); 
      if (allPageSelected) {
        pageIds.forEach(id => next.delete(id));
      } else {
        pageIds.forEach(id => next.add(id));
      }
      return next; 
    }); 
  };
  
  const toggleOne = (id) => { 
    setSelected(prev => { 
      const n = new Set(prev); 
      if (n.has(id)) {
        n.delete(id);
      } else {
        n.add(id);
      }
      return n; 
    }); 
  };
  
  const clearSelection = () => setSelected(new Set());

  const handleBulkAction = async (action) => {
    if (selected.size === 0) {
      showToast("Please select at least one driver", "error");
      return;
    }

    setActionLoading(true);
    try {
      const ids = Array.from(selected);
      let result;
      if (action === 'login allowed' || action === 'logout blocked') {
        result = await postData('drivers/Riders/login-access', {
          ids,
          blocked: action === 'logout blocked',
        });
        if (!result?.success && !result?.status) throw new Error(result?.message || 'Login access update failed');
      } else {
        const isReady = action === 'duty turned on';
        const results = await Promise.all(ids.map((driverId) =>
          postData('driver/driver/toggle-duty', { driverId, isReady }),
        ));
        const failed = results.find((item) => !item?.success);
        if (failed) throw new Error(failed.message || 'Duty update failed');
      }

      showToast(`${selected.size} driver(s) ${action} successfully`);
      clearSelection();
      await fetchDrivers();
    } catch (err) {
      showToast(`Failed to ${action}: ${err.message}`, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDriverNavigate = () => {
    if (!hasCreatePermission) {
      showToast("You don't have permission to add drivers", "error");
      return;
    }
    navigate("/dashboard/drivers/create", { state: { mode: "add" } });
  };

  const handleEditDriverNavigate = (driver) => {
    if (!hasEditPermission) {
      showToast("You don't have permission to edit drivers", "error");
      return;
    }
    navigate("/dashboard/drivers/Edit", { state: { mode: "edit", driverData: driver } });
  };

  const handleViewMoreClick = (driver) => {
    if (!hasOverviewPermission) {
      showToast("You don't have permission to view driver details", "error");
      return;
    }
    navigate("/dashboard/drivers/view", { state: { driver } });
  };

  const handleDeleteSelected = async () => {
    if (!hasDeletePermission) {
      showToast("You don't have permission to delete drivers", "error");
      return;
    }
    if (selected.size === 0) return;
    setActionLoading(true);
    try { 
      showToast(`${selected.size} driver(s) deleted`); 
      clearSelection(); 
      setConfirmDelete(false); 
      fetchDrivers(); 
    } catch (err) { 
      showToast(`Delete failed: ${err.message}`, "error"); 
    } finally { 
      setActionLoading(false); 
    }
  };

  const goToLastPage = () => {
    if (totalPages > 0) {
      setPage(totalPages);
    }
  };

  const goToFirstPage = () => {
    setPage(1);
  };

  const exportToCSV = () => {
    if (!hasOverviewPermission) {
      showToast("You don't have permission to export driver data", "error");
      return;
    }
    const headers = ['SN', 'ID', 'Login Access', 'Date', 'Duty Ready', 'Account', 'Driver Name', 'Contact', 'Verified', 'Wallet'];
    const csvData = mappedDrivers.map(d => [
      d.sn, d.id, String(d.login_blocked) === "1" ? "Blocked" : "Allowed", d.displayDate, d.is_ready === "1" ? "Yes" : "No", 
      d.open_status === "1" ? "ON" : "OFF", d.displayName, d.contact, 
      (d.is_verify_personalinfo === "1" && d.is_verify_docinfo === "1" && d.is_verify_vehicle === "1" && d.is_verify_bankinfo === "1" && d.is_verify_emergency === "1") ? "Yes" : "No", 
      d.displayWallet
    ]);
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `drivers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Exported successfully");
  };

  const columnWidths = {
    checkbox: "32px",
    sn: "40px",
    id: "50px",
    pic: "44px",
    status: "70px",
    date: "85px",
    ready: "55px",
    onoff: "55px",
    name: "120px",
    contact: "95px",
    verification: "75px",
    wallet: "75px",
    actions: "85px"
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STYLES (Using theme object - Same as Restaurant)
  // ─────────────────────────────────────────────────────────────────────────────
  const styles = {
    container: { 
      minHeight: "100vh", 
      background: theme.background, 
      fontFamily: "'DM Sans', sans-serif", 
      padding: "16px 20px",
      transition: "background 0.3s ease"
    },
    header: { 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center", 
      marginBottom: "16px", 
      flexWrap: "wrap", 
      gap: "12px" 
    },
    headerLeft: { 
      display: "flex", 
      alignItems: "center", 
      gap: "12px", 
      flexWrap: "wrap" 
    },
    backBtn: { 
      display: "flex", 
      alignItems: "center", 
      gap: "6px", 
      padding: "6px 14px", 
      borderRadius: "8px", 
      border: `1px solid ${theme.border}`, 
      background: "transparent", 
      color: theme.textSecondary, 
      cursor: "pointer", 
      fontSize: "12px", 
      transition: "all 0.2s" 
    },
    filterBadge: { 
      display: "inline-flex", 
      alignItems: "center", 
      gap: "6px", 
      padding: "4px 12px", 
      borderRadius: "20px", 
      fontSize: "11px", 
      fontWeight: 600, 
      background: `${theme.success}1a`, 
      color: theme.success, 
      border: `1px solid ${theme.success}30` 
    },
    title: { 
      fontSize: "18px", 
      fontWeight: 700, 
      color: theme.textPrimary, 
      margin: 0 
    },
    subtitle: { 
      fontSize: "11px", 
      color: theme.textMuted, 
      marginTop: "2px" 
    },
    headerActions: { 
      display: "flex", 
      gap: "10px", 
      flexWrap: "wrap" 
    },
    addBtn: { 
      background: hasCreatePermission ? theme.primary : theme.textMuted, 
      color: "white", 
      border: "none", 
      padding: "8px 16px", 
      borderRadius: "8px", 
      cursor: hasCreatePermission ? "pointer" : "not-allowed", 
      fontWeight: 600, 
      fontSize: "12px", 
      display: "flex", 
      alignItems: "center", 
      gap: "5px",
      opacity: hasCreatePermission ? 1 : 0.5,
    },
    exportBtn: { 
      background: hasOverviewPermission ? theme.success : theme.textMuted, 
      color: "white", 
      border: "none", 
      padding: "8px 16px", 
      borderRadius: "8px", 
      cursor: hasOverviewPermission ? "pointer" : "not-allowed", 
      fontWeight: 600, 
      fontSize: "12px",
      opacity: hasOverviewPermission ? 1 : 0.5,
    },
    filtersBar: { 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center", 
      marginBottom: "16px", 
      flexWrap: "wrap", 
      gap: "12px" 
    },
    searchWrapper: { 
      position: "relative", 
      flex: 1, 
      maxWidth: "280px" 
    },
    searchInput: { 
      width: "100%", 
      padding: "8px 10px 8px 32px", 
      background: theme.surface, 
      border: `1px solid ${theme.border}`, 
      borderRadius: "8px", 
      fontSize: "12px", 
      color: theme.textPrimary, 
      outline: "none", 
      transition: "all 0.2s" 
    },
    searchIcon: { 
      position: "absolute", 
      left: "10px", 
      top: "50%", 
      transform: "translateY(-50%)", 
      color: theme.primary 
    },
    filterControls: { 
      display: "flex", 
      gap: "15px", 
      alignItems: "center", 
      flexWrap: "wrap" 
    },
    filterGroup: { 
      display: "flex", 
      alignItems: "center", 
      gap: "6px", 
      fontSize: "11px", 
      color: theme.textSecondary 
    },
    filterButtons: { 
      display: "flex", 
      gap: "4px" 
    },
    filterBtn: (isActive) => ({ 
      padding: "3px 8px", 
      borderRadius: "5px", 
      fontSize: "11px", 
      background: isActive ? theme.primary : "transparent", 
      color: isActive ? "#fff" : theme.textSecondary, 
      border: isActive ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`, 
      cursor: "pointer",
      transition: "all 0.2s"
    }),
    tableWrapper: { 
      background: theme.surface, 
      border: `1px solid ${theme.border}`, 
      borderRadius: "12px", 
      overflow: "hidden", 
      width: "100%", 
      transition: "all 0.3s ease" 
    },
    table: { 
      width: "100%", 
      borderCollapse: "collapse", 
      tableLayout: "fixed" 
    },
    th: (width) => ({ 
      padding: "10px 6px", 
      textAlign: "left", 
      fontSize: "10px", 
      fontWeight: 700, 
      color: theme.primary, 
      borderBottom: `1px solid ${theme.border}`, 
      background: theme.primaryLight, 
      width: width || "auto", 
      whiteSpace: "nowrap",
      transition: "all 0.2s"
    }),
    td: { 
      padding: "8px 6px", 
      borderBottom: `1px solid ${theme.border}`, 
      fontSize: "11px", 
      color: theme.textPrimary, 
      whiteSpace: "nowrap", 
      overflow: "hidden", 
      textOverflow: "ellipsis" 
    },
    pagination: { 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "space-between", 
      padding: "10px 16px", 
      borderTop: `1px solid ${theme.border}`, 
      background: theme.primaryLight, 
      flexWrap: "wrap", 
      gap: "10px" 
    },
    viewMoreBtn: { 
      background: hasOverviewPermission ? theme.primary : theme.textMuted, 
      color: "white", 
      border: "none", 
      padding: "3px 8px", 
      borderRadius: "4px", 
      cursor: hasOverviewPermission ? "pointer" : "not-allowed", 
      fontSize: "9px", 
      fontWeight: 500, 
      whiteSpace: "nowrap",
      opacity: hasOverviewPermission ? 1 : 0.6,
    },
    selectionBar: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      background: `${theme.primary}12`,
      border: `1px solid ${theme.primary}40`,
      borderRadius: "12px",
      padding: "10px 16px",
      marginBottom: "14px",
      flexWrap: "wrap",
      backdropFilter: "blur(8px)",
      boxShadow: `0 4px 20px ${theme.primary}15`,
      animation: "slideDown 0.3s ease",
      position: "relative",
      overflow: "hidden",
    },
    actionButtons: {
      display: "flex",
      gap: "6px",
      flexWrap: "wrap",
      flex: 1,
      alignItems: "center",
    },
    actionBtn: (color, bgColor, disabled = false) => ({
      padding: "5px 12px",
      borderRadius: "6px",
      border: "none",
      background: disabled ? theme.textMuted : (bgColor || `${theme.primary}1a`),
      color: disabled ? theme.textMuted : (color || theme.primary),
      fontSize: "10px",
      fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      position: "relative",
      overflow: "hidden",
      opacity: disabled ? 0.5 : 1,
    }),
    rowsPerPageSelect: {
      padding: "2px 20px 2px 8px",
      borderRadius: "4px",
      border: `1px solid ${theme.border}`,
      background: theme.surface,
      color: theme.textPrimary,
      fontSize: "10px",
      cursor: "pointer",
      outline: "none",
      appearance: "auto",
      fontFamily: "'DM Sans', sans-serif",
      height: "24px"
    },
    countBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      padding: "2px 10px",
      borderRadius: "12px",
      fontSize: "10px",
      fontWeight: 600,
      background: `${theme.primary}1a`,
      color: theme.primary
    },
    permissionDenied: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      textAlign: "center",
      padding: "40px",
    },
    permissionDeniedTitle: {
      fontSize: "24px",
      fontWeight: 700,
      color: theme.textPrimary,
      marginBottom: "12px",
    },
    permissionDeniedText: {
      fontSize: "14px",
      color: theme.textMuted,
      maxWidth: "400px",
    },
    permissionDeniedIcon: {
      fontSize: "48px",
      marginBottom: "16px",
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button 
          key={i} 
          onClick={() => setPage(i)} 
          style={{ 
            width: "26px", 
            height: "26px", 
            borderRadius: "5px", 
            border: page === i ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`, 
            background: page === i ? theme.primaryLight : theme.surface, 
            color: page === i ? theme.primary : theme.textSecondary, 
            cursor: "pointer", 
            fontSize: "10px" 
          }}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (!hasOverviewPermission) {
    return (
      <div style={styles.container}>
        <div style={styles.permissionDenied}>
          <div style={styles.permissionDeniedIcon}>🔒</div>
          <div style={styles.permissionDeniedTitle}>Access Denied</div>
          <div style={styles.permissionDeniedText}>
            You don't have permission to view driver data. Please contact your administrator.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slideUp { from{transform:translateY(16px);opacity:0} to{transform:translateY(0);opacity:1} } 
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        input::placeholder { color: ${theme.textMuted}; }
        .action-btn {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        .action-btn:disabled { opacity: 0.5; cursor: not-allowed !important; }
        .action-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(255,255,255,0.15), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        .action-btn:hover::after { opacity: 1; }
        .action-btn:active { transform: scale(0.95) !important; }
        .clear-btn {
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .clear-btn:hover { 
          background: ${theme.primaryLight} !important; 
          color: ${theme.textPrimary} !important; 
        }
        @media (max-width: 1200px) { .driver-table { font-size: 10px; } }
      `}</style>

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button style={styles.backBtn} onClick={handleBack}>
            <BackIcon /> Back
          </button>
          <div>
            <h1 style={styles.title}>
              {isPreFiltered ? filterTitle : "Drivers"}
            </h1>
            <p style={styles.subtitle}>
              {isPreFiltered 
                ? `Showing ${drivers.length} ${filterTitle.toLowerCase()}`
                : "Manage and track all delivery drivers"}
            </p>
          </div>
          {isPreFiltered && (
            <span style={styles.filterBadge}>
              <OnlineIcon /> {drivers.length} Online
            </span>
          )}
          {!isPreFiltered && (
            <span style={styles.countBadge}>
              Total: {drivers.length} Drivers
            </span>
          )}
        </div>
        <div style={styles.headerActions}>
          <button 
            style={styles.exportBtn} 
            onClick={exportToCSV}
            disabled={!hasOverviewPermission}
          >
            📊 Export CSV
          </button>
          <button 
            style={styles.addBtn} 
            onClick={handleAddDriverNavigate}
            disabled={!hasCreatePermission}
          >
            <PlusIcon /> Add Driver
          </button>
        </div>
      </div>

      <div style={styles.filtersBar}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}><SearchIcon /></span>
          <input 
            type="text" 
            style={styles.searchInput} 
            placeholder="Search by Name, ID, Contact, City, Bank..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
          />
        </div>
        <div style={styles.filterControls}>
          <div style={styles.filterGroup}>
            <label>Login:</label>
            <div style={styles.filterButtons}>
              <button style={styles.filterBtn(statusFilter === "All")} onClick={() => { setStatusFilter("All"); setPage(1); }}>All</button>
              <button style={styles.filterBtn(statusFilter === "Allowed")} onClick={() => { setStatusFilter("Allowed"); setPage(1); }}>Allowed</button>
              <button style={styles.filterBtn(statusFilter === "Blocked")} onClick={() => { setStatusFilter("Blocked"); setPage(1); }}>Blocked</button>
            </div>
          </div>
          <div style={styles.filterGroup}>
            <label>Duty:</label>
            <div style={styles.filterButtons}>
              <button style={styles.filterBtn(readyFilter === "All")} onClick={() => { setReadyFilter("All"); setPage(1); }}>All</button>
              <button style={styles.filterBtn(readyFilter === "Yes")} onClick={() => { setReadyFilter("Yes"); setPage(1); }}>Yes</button>
              <button style={styles.filterBtn(readyFilter === "No")} onClick={() => { setReadyFilter("No"); setPage(1); }}>No</button>
            </div>
          </div>
        </div>
      </div>

      {selected.size > 0 && hasDeletePermission && (
        <div style={styles.selectionBar}>
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)",
            animation: "shimmer 3s infinite",
            pointerEvents: "none",
          }} />
          
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            paddingRight: "14px",
            borderRight: `1px solid ${theme.border}`,
            flexShrink: 0,
          }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: theme.primaryGradient || "linear-gradient(135deg, #3b82f6, #8b5cf6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "13px",
              fontWeight: 700,
              color: "#fff",
              boxShadow: `0 2px 8px ${theme.primary}50`,
            }}>
              {selected.size}
            </div>
            <div>
              <span style={{
                fontSize: "13px",
                fontWeight: 600,
                color: theme.textPrimary,
              }}>
                Driver{selected.size > 1 ? "s" : ""} Selected
              </span>
              <span style={{
                fontSize: "10px",
                color: theme.textMuted,
                display: "block",
                marginTop: "-1px",
              }}>
                {selected.size === 1 ? "Click actions below to manage" : `${selected.size} items ready for bulk action`}
              </span>
            </div>
          </div>
          
          <div style={styles.actionButtons}>
            <button 
              className="action-btn"
              onClick={() => handleBulkAction('login allowed')}
              disabled={actionLoading}
              style={styles.actionBtn(theme.success, `${theme.success}1a`)}
            >
              <CheckCircleIcon /> Allow Login
            </button>
            
            <button 
              className="action-btn"
              onClick={() => handleBulkAction('logout blocked')}
              disabled={actionLoading}
              style={styles.actionBtn(theme.error, `${theme.error}1a`)}
            >
              <XCircleIcon /> Logout & Block
            </button>
            
            <div style={{ width: "1px", height: "24px", background: theme.border }} />
            
            <button 
              className="action-btn"
              onClick={() => handleBulkAction('duty turned on')}
              disabled={actionLoading}
              style={styles.actionBtn(theme.success, `${theme.success}1a`)}
            >
              <PowerIcon /> Duty ON (Ready)
            </button>
            
            <button 
              className="action-btn"
              onClick={() => handleBulkAction('duty turned off')}
              disabled={actionLoading}
              style={styles.actionBtn(theme.textMuted, `${theme.textMuted}1a`)}
            >
              <PowerIcon /> Duty OFF (Not Ready)
            </button>
            
            <div style={{ width: "1px", height: "24px", background: theme.border }} />
            
            <button 
              className="action-btn"
              onClick={() => setConfirmDelete(true)}
              disabled={actionLoading}
              style={styles.actionBtn(theme.error, `${theme.error}1a`)}
            >
              <TrashIcon /> Delete
            </button>
          </div>
          
          <button 
            onClick={clearSelection} 
            className="clear-btn"
            style={{
              padding: "4px 12px",
              borderRadius: "6px",
              border: `1px solid ${theme.border}`,
              background: "transparent",
              color: theme.textSecondary,
              fontSize: "11px",
              fontWeight: 500,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            Clear
          </button>
        </div>
      )}

      <div style={styles.tableWrapper}>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {hasDeletePermission && (
                  <th style={styles.th(columnWidths.checkbox)}><Checkbox checked={allPageSelected} onChange={toggleAll} theme={theme} /></th>
                )}
                <th style={styles.th(columnWidths.sn)}>SN</th>
                <th style={styles.th(columnWidths.id)}>ID</th>
                <th style={styles.th(columnWidths.pic)}>Pic</th>
                <th style={styles.th(columnWidths.status)}>Login</th>
                <th style={styles.th(columnWidths.date)}>Date</th>
                <th style={styles.th(columnWidths.ready)}>Duty</th>
                <th style={styles.th(columnWidths.onoff)}>Account</th>
                <th style={styles.th(columnWidths.name)}>Driver Name</th>
                <th style={styles.th(columnWidths.contact)}>Contact</th>
                <th style={styles.th(columnWidths.verification)}>Verification</th>
                <th style={styles.th(columnWidths.wallet)}>Wallet</th>
                <th style={styles.th(columnWidths.actions)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: perPage }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: hasDeletePermission ? 13 : 12 }).map((_, j) => (
                      <td key={j} style={styles.td}>
                        <div style={{ height: "10px", background: theme.border, borderRadius: "3px", width: j === 0 ? "12px" : "70%" }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : mappedDrivers.length === 0 ? (
                <tr>
                  <td colSpan={hasDeletePermission ? 13 : 12} style={{ padding: "50px", textAlign: "center", color: theme.textMuted, fontSize: "12px" }}>
                    {isPreFiltered ? `No ${filterTitle.toLowerCase()} found` : debouncedSearch ? `No drivers found matching "${debouncedSearch}"` : "No drivers found"}
                  </td>
                </tr>
              ) : (
                mappedDrivers.map((driver) => (
                  <tr key={driver.id} style={{ background: selected.has(driver.id?.toString()) ? theme.primaryLight : "transparent" }}>
                    {hasDeletePermission && (
                      <td style={styles.td}><Checkbox checked={selected.has(driver.id?.toString())} onChange={() => toggleOne(driver.id?.toString())} theme={theme} /></td>
                    )}
                    <td style={styles.td}>{driver.sn}</td>
                    <td style={styles.td}>{driver.id}</td>
                    <td style={styles.td}>
                      <DriverImage 
                        picName={driver.pic} 
                        name={driver.displayName} 
                        theme={theme} 
                      />
                    </td>
                    <td style={styles.td}><StatusBadge loginBlocked={driver.login_blocked} theme={theme} /></td>
                    <td style={styles.td}>{driver.displayDate}</td>
                    <td style={styles.td}><ReadyBadge is_ready={driver.is_ready} theme={theme} /></td>
                    <td style={styles.td}><OnOffBadge open_status={driver.open_status} theme={theme} /></td>
                    <td style={styles.td}><strong style={{ fontSize: "11px", color: theme.textPrimary }}>{driver.displayName}</strong></td>
                    <td style={styles.td}>{driver.contact}</td>
                    <td style={styles.td}><VerificationBadge driver={driver} theme={theme} /></td>
                    <td style={styles.td}>{driver.displayWallet}</td>
                    <td style={styles.td}>
                      <button style={styles.viewMoreBtn} onClick={() => handleViewMoreClick(driver)}>
                        <ViewMoreIcon /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={styles.pagination}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "10px", color: theme.textMuted }}>
                {loading ? "Loading..." : `Showing ${startIndex + 1}–${Math.min(startIndex + perPage, totalFiltered)} of ${totalFiltered} drivers`}
              </span>
              
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontSize: "10px", color: theme.textMuted }}>Rows:</span>
                <select 
                  value={perPage} 
                  onChange={handlePerPageChange}
                  style={styles.rowsPerPageSelect}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                </select>
              </div>
            </div>
            
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              <button 
                onClick={goToFirstPage} 
                disabled={page === 1 || loading} 
                style={{ 
                  width: "26px", 
                  height: "26px", 
                  borderRadius: "5px", 
                  border: `1px solid ${theme.border}`, 
                  background: theme.surface, 
                  color: page === 1 ? theme.textMuted : theme.textSecondary, 
                  cursor: "pointer", 
                  fontSize: "9px", 
                  fontWeight: 700 
                }}
                title="First Page"
              >
                ««
              </button>
              
              <button 
                onClick={() => setPage(p => Math.max(1, p - 1))} 
                disabled={page === 1 || loading} 
                style={{ 
                  width: "26px", 
                  height: "26px", 
                  borderRadius: "5px", 
                  border: `1px solid ${theme.border}`, 
                  background: theme.surface, 
                  color: page === 1 ? theme.textMuted : theme.textSecondary, 
                  cursor: "pointer", 
                  fontSize: "10px" 
                }}
              >
                <ChevronLeftIcon />
              </button>
              
              {renderPageNumbers()}
              
              <button 
                onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                disabled={page === totalPages || loading} 
                style={{ 
                  width: "26px", 
                  height: "26px", 
                  borderRadius: "5px", 
                  border: `1px solid ${theme.border}`, 
                  background: theme.surface, 
                  color: page === totalPages ? theme.textMuted : theme.textSecondary, 
                  cursor: "pointer", 
                  fontSize: "10px" 
                }}
              >
                <ChevronRightIcon />
              </button>
              
              <button 
                onClick={goToLastPage} 
                disabled={page === totalPages || loading || totalPages === 0} 
                style={{ 
                  width: "26px", 
                  height: "26px", 
                  borderRadius: "5px", 
                  border: `1px solid ${theme.border}`, 
                  background: theme.surface, 
                  color: (page === totalPages || totalPages === 0) ? theme.textMuted : theme.textSecondary, 
                  cursor: (page === totalPages || totalPages === 0) ? "not-allowed" : "pointer", 
                  fontSize: "9px", 
                  fontWeight: 700 
                }}
                title="Last Page"
              >
                »»
              </button>
            </div>
          </div>
        )}
      </div>

      {confirmDelete && hasDeletePermission && (
        <div onClick={() => setConfirmDelete(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "12px", padding: "20px", width: "320px" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: "14px", color: theme.textPrimary }}>Confirm Delete</h3>
            <p style={{ fontSize: "12px", color: theme.textSecondary, marginBottom: "16px" }}>
              Delete {selected.size} driver{selected.size > 1 ? "s" : ""}? This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "8px" }}>
              <button 
                onClick={() => setConfirmDelete(false)} 
                style={{ 
                  flex: 1, 
                  padding: "8px", 
                  borderRadius: "6px", 
                  border: `1px solid ${theme.border}`, 
                  background: theme.surface, 
                  color: theme.textSecondary, 
                  cursor: "pointer", 
                  fontSize: "11px" 
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteSelected} 
                disabled={actionLoading} 
                style={{ 
                  flex: 1, 
                  padding: "8px", 
                  borderRadius: "6px", 
                  border: "none", 
                  background: theme.error, 
                  color: "white", 
                  cursor: "pointer", 
                  fontSize: "11px" 
                }}
              >
                {actionLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} theme={theme} />}
    </div>
  );
};

export default DriverTable;
